import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { MAIN_SITE_URL } from '@/mocks/categories';

export default function Header() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [
    { label: t('home.navHome'), to: '/' },
    { label: t('home.navSpreadsheet'), to: '/' },
    { label: t('home.navGuides'), to: '/usfans-spreadsheet-beginner-guide' },
    { label: t('home.navShoppingGuide'), to: '/guide/usfans-spreadsheet-complete-guide' },
    { label: t('home.navReviews'), to: '/guide/usfans-review-2026' },
    { label: t('home.navShipping'), to: '/guide/usfans-shipping-cost-guide' },
    { label: t('home.navSafety'), to: '/guide/usfans-safety-guide' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-stone-200">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 md:px-6 h-16">
        <Link to="/" className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
          <span className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
            <i className="ri-table-line text-lg" />
          </span>
          <span className="font-heading font-bold text-stone-900 text-base">USFans Spreadsheet</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {nav.map((item) =>
            item.to === '/' ? (
              <Link
                key={item.label}
                to={item.to}
                className="text-sm text-stone-700 hover:text-emerald-700 whitespace-nowrap cursor-pointer font-semibold"
              >
                {item.label}
              </Link>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className="text-sm text-stone-600 hover:text-emerald-700 whitespace-nowrap cursor-pointer font-medium"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 whitespace-nowrap cursor-pointer"
          >
            {t('home.navVisit')}
          </a>
        </div>

        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-stone-700 cursor-pointer"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 top-16 z-40 bg-white">
          <div className="h-full overflow-y-auto px-5 py-5">
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mt-2 mb-1">{t('home.navCategories')}</p>
              {nav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 text-base text-stone-800 hover:text-emerald-700 cursor-pointer border-b border-stone-100 font-medium"
                >
                  {item.label}
                </Link>
              ))}

              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mt-4 mb-1">{t('home.catPlaceholder')}</p>
              {[
                { key: 'home.catShoes', link: 'https://kaspreadsheet.com/category-911-Shoes.html' },
                { key: 'home.catHoodies', link: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
                { key: 'home.catTshirts', link: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
                { key: 'home.catJackets', link: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
                { key: 'home.catPants', link: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
                { key: 'home.catHeadwear', link: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
                { key: 'home.catSets', link: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
                { key: 'home.catUnderwear', link: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
                { key: 'home.catJersey', link: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
                { key: 'home.catAccessories', link: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
                { key: 'home.catBags', link: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
                { key: 'home.catBelts', link: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
                { key: 'home.catPerfumes', link: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
                { key: 'home.catOther', link: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
              ].map((cat) => (
                <a
                  key={cat.key}
                  href={cat.link}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="py-2.5 text-sm text-stone-600 hover:text-emerald-700 cursor-pointer flex items-center gap-2"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  {t(cat.key)}
                </a>
              ))}

              <div className="mt-6 pt-4 border-t border-stone-100">
                <LanguageSwitcher />
              </div>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-4 rounded-md bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 text-center cursor-pointer"
              >
                {t('home.navVisit')}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}